import { motion } from "framer-motion";
import { ShieldCheck, FileCheck2, Users, Droplets, BadgeCheck } from "lucide-react";
import { IMAGES, VIDEOS } from "@/data/content";
import VideoBackdrop from "@/components/VideoBackdrop";

const POINTS = [
  {
    icon: ShieldCheck,
    title: "Independently accredited",
    text: "An IANZ-accredited laboratory, assessed to ISO/IEC 17025:2017 — the standard auditors look for.",
  },
  {
    icon: Droplets,
    title: "Blue Tick accredited",
    text: "Irrigation NZ Blue Tick water-measurement accreditation for all flow meter installations, verifications and calibrations.",
    badge: { src: "/blue-tick.png", alt: "Irrigation New Zealand Blue Tick — Accredited Service Provider, Water measurement" },
  },
  {
    icon: BadgeCheck,
    title: "IQP registered",
    text: "Independently Qualified Person (IQP) registered for Auckland, Waikato and Wellington building compliance work.",
  },
  {
    icon: FileCheck2,
    title: "Certificates that stand up",
    text: "Every job comes with a traceable calibration certificate linked to national measurement standards.",
  },
  {
    icon: Users,
    title: "Talk to the technicians",
    text: "You deal directly with the people calibrating your instruments — no call centres, no run-around.",
  },
];

export default function WhyAci() {
  return (
    <section id="why-aci" data-testid="why-aci-section" className="bg-base py-20 md:py-24">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-2 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <p className="hud-label">[ 03 / WHY ACI ]</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold uppercase tracking-tight text-ink md:text-4xl lg:text-5xl">
            Precision you can put in front of an auditor
          </h2>
          <div className="mt-10 space-y-6">
            {POINTS.map(({ icon: Icon, title, text, badge }, i) => (
              <div key={i} data-testid={`why-point-${i}`} className="flex gap-5">
                <div className="relative flex h-11 w-11 shrink-0 items-center justify-center border border-brand/25 bg-brand/10">
                  <Icon className="h-5 w-5 text-brand" aria-hidden="true" />
                  <span aria-hidden="true" className="absolute -bottom-1 -left-1 h-2 w-2 border-b border-l border-brand/60" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-xl font-medium tracking-tight text-ink">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-body">{text}</p>
                  {badge && (
                    <span className="mt-3 inline-flex rounded-sm border border-line/10 bg-white px-3 py-2" data-testid="blue-tick-badge">
                      <img src={badge.src} alt={badge.alt} loading="lazy" className="h-12 w-auto" />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
          className="flex flex-col"
        >
          <div className="corner-brackets relative min-h-[240px] flex-1 overflow-hidden border border-line/10 bg-[#0A0F1A]">
            <VideoBackdrop
              sources={VIDEOS.labWork.sources}
              poster={IMAGES.technician}
              alt="Engineer adjusting industrial pipeline instrumentation"
              rate={0.5}
              className="opacity-70"
              testId="why-aci-video-backdrop"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A]/90 via-transparent to-transparent" />
            <span className="hud-label photo-tile-label absolute bottom-4 left-4">LAB.OPS // NGĀRUAWĀHIA</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
